import { useCallback, useEffect, useRef, useState } from "react";
import { isOcrScopeError } from "./useOcrBusinessUnitScope";

// File objects stay lazy: only the current file is put in a multipart request.
// A confirmed import keeps its ID for OCR retries and releases its File reference.
export default function useOcrUploadQueue({ api, enabled, canContinue, onRefresh, retryFailedReads = true, basePath = "/api/purchasing/ocr-imports" }) {
  const options = useRef({ api, enabled, canContinue, onRefresh, basePath });
  options.current = { api, enabled, canContinue, onRefresh, basePath };
  const mounted = useRef(true);
  const generation = useRef(0);
  const nextId = useRef(0);
  const state = useRef({ rows: [], running: false, paused: false });
  const [view, setView] = useState(state.current);
  const publish = useCallback(() => {
    if (mounted.current) setView({ ...state.current, rows: state.current.rows.map(({ file, ...row }) => ({ ...row })) });
  }, []);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; generation.current += 1; state.current.rows = []; };
  }, []);
  useEffect(() => {
    generation.current += 1;
    state.current = { rows: [], running: false, paused: false };
    publish();
  }, [api, enabled, basePath, publish]);

  const pump = useCallback(async () => {
    if (state.current.running) return;
    const token = generation.current;
    const scopedApi = options.current.api;
    const alive = () => mounted.current && generation.current === token;
    const permitted = () => alive() && options.current.enabled && options.current.api === scopedApi && scopedApi.isCurrent() && options.current.canContinue();
    if (!permitted()) return;
    state.current.running = true;
    publish();
    try {
      while (permitted() && !state.current.paused) {
        const row = state.current.rows.find((entry) => entry.status === "queued");
        if (!row) break;
        row.error = "";
        try {
          if (!row.importId) {
            row.status = "uploading";
            publish();
            const form = new FormData();
            form.append("file", row.file);
            form.append("source", "manual_upload");
            form.append("auto_process", "false");
            const { data } = await scopedApi.post(`${options.current.basePath}/upload`, form, {
              headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
            });
            if (!alive()) return;
            const id = Number(data?.import_id || data?.id);
            if (!Number.isSafeInteger(id) || id <= 0) throw new Error("Upload not confirmed");
            row.importId = id;
            row.file = null;
          }
          if (!permitted()) { row.status = "processing"; break; }
          row.status = "reading";
          publish();
          const process = (force) => scopedApi.post(`${options.current.basePath}/${row.importId}/process`, {
            wait: true, force, max_wait_ms: 180000, poll_interval_ms: 3000,
          }, { timeout: 210000 });
          let { data } = await process(false);
          if (!alive()) return;
          // Revalidate the server status before restarting a failed job. Another
          // user may already have completed/reviewed it since the previous attempt.
          if (row.retryReading && data?.skipped && data?.import?.status === "failed" && permitted()) {
            ({ data } = await process(true));
            if (!alive()) return;
          }
          const status = data?.import?.status || data?.ocr_status;
          if (data?.failed || status === "failed") {
            row.status = "read_failed";
            row.error = data?.import?.error_message || "Could not read this document.";
          } else if (!data?.timed_out && (["ready", "needs_review", "created", "confirmed"].includes(status) || data?.completed)) {
            row.status = "ready";
          } else {
            row.status = "processing";
          }
        } catch (error) {
          if (!alive()) return;
          if (isOcrScopeError(error) || !permitted()) break;
          const status = Number(error?.response?.status);
          const rejected = status >= 400 && status < 500 && ![408, 499].includes(status);
          row.status = row.importId ? "read_failed" : rejected ? "upload_failed" : "uncertain";
          row.error = error?.response?.data?.error || error?.message || "Could not upload this document.";
          // A lost response/5xx may follow a successful INSERT. Never repeat it.
          if (row.status === "uncertain") row.file = null;
          if (status === 401 || status === 403) state.current.paused = true;
        }
        if (!permitted()) break;
        publish();
        // Refresh errors cannot change a successfully uploaded file into a failure.
        try { await options.current.onRefresh?.(); } catch (_) { /* Inbox offers its own retry. */ }
      }
    } finally {
      if (alive()) {
        state.current.running = false;
        publish();
      }
    }
  }, [publish]);

  const enqueue = useCallback((files) => {
    if (!options.current.enabled || !options.current.api.isCurrent() || !options.current.canContinue()) return;
    const selected = Array.from(files || []);
    if (!selected.length) return;
    const hasPending = state.current.rows.some((row) => row.status === "queued");
    if (!state.current.running && !hasPending) state.current.paused = false;
    state.current.rows.push(...selected.map((file) => ({ id: ++nextId.current, file, name: file.name, size: file.size, status: "queued", importId: null, error: "" })));
    publish();
    void pump();
  }, [publish, pump]);
  const pause = () => { state.current.paused = true; publish(); };
  const resume = () => { state.current.paused = false; publish(); void pump(); };
  const cancelPending = () => {
    state.current.rows.forEach((row) => { if (row.status === "queued") { row.status = "cancelled"; row.file = null; } });
    publish();
  };
  const retry = (statuses) => {
    state.current.rows.forEach((row) => {
      if (statuses.includes(row.status)) {
        row.retryReading = row.status === "read_failed";
        row.status = "queued";
      }
    });
    resume();
  };
  const pending = view.rows.filter((row) => row.status === "queued").length;
  const busy = view.running || pending > 0;
  useEffect(() => {
    if (!busy || !enabled) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [busy, enabled]);
  const retryableStatuses = retryFailedReads ? ["upload_failed", "read_failed"] : ["upload_failed"];
  return { ...view, pending, busy, enqueue, pause, resume, cancelPending,
    canRetryFailed: view.rows.some(row => retryableStatuses.includes(row.status)),
    retryFailed: () => retry(retryableStatuses),
    checkPending: () => retry(["processing"]),
  };
}
